import React, { useEffect, useState } from 'react';
import { XIcon, CalendarPlusIcon, LockIcon, MailIcon, PlusIcon, AlertCircleIcon } from 'lucide-react';
import { MediaType, BrandContact, Store } from '../../data/types';
import { brands } from '../../data/brands';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
interface PreBookModalProps {
  isOpen: boolean;
  mode: 'prebook' | 'block';
  selectedCount: number;
  stores?: Store[];
  onClose: () => void;
  onConfirm: (mediaTypes: MediaType[], weekNumbers: number[], brandName?: string, contactInfo?: {
    name: string;
    email: string;
    jobTitle?: string;
  }) => void;
}
const allMediaTypes: MediaType[] = ['Digital Outposts', 'Digital Drive Aisle', 'Digital Windows'];
const weeks = Array.from({
  length: 12
}, (_, i) => i + 1);
export function PreBookModal({
  isOpen,
  mode,
  selectedCount,
  stores = [],
  onClose,
  onConfirm
}: PreBookModalProps) {
  const [selectedMediaTypes, setSelectedMediaTypes] = useState<MediaType[]>([]);
  const [startWeek, setStartWeek] = useState<number | ''>('');
  const [endWeek, setEndWeek] = useState<number | ''>('');
  const [brandName, setBrandName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Contact state (prebook mode)
  const [contacts, setContacts] = useState<BrandContact[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    jobTitle: ''
  });
  // Conflict detection
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);
  // Load contacts when brand changes
  useEffect(() => {
    if (brandName) {
      const brand = brands.find(b => b.name === brandName);
      const defaultContacts = brand?.contacts || [];
      const storedContactsStr = localStorage.getItem(`brand-contacts-${brand?.id}`);
      const storedContacts: BrandContact[] = storedContactsStr ? JSON.parse(storedContactsStr) : [];
      const merged = [...defaultContacts];
      storedContacts.forEach(sc => {
        if (!merged.find(mc => mc.id === sc.id)) {
          merged.push(sc);
        }
      });
      setContacts(merged);
      if (merged.length > 0) {
        setSelectedContactId(merged[0].id);
      } else {
        setSelectedContactId('');
      }
      setIsAddingContact(merged.length === 0);
    } else {
      setContacts([]);
      setSelectedContactId('');
      setIsAddingContact(false);
    }
  }, [brandName]);
  useEffect(() => {
    if (mode === 'prebook' && brandName && startWeek !== '' && endWeek !== '') {
      const start = Number(startWeek);
      const end = Number(endWeek);
      if (start <= end) {
        // Check if brand already has bookings in these weeks
        const conflictingStores = new Set<string>();
        stores.forEach(store => {
          store.slots.forEach(slot => {
            if (slot.status === 'booked' && slot.brandName === brandName && slot.weekNumber >= start && slot.weekNumber <= end) {
              conflictingStores.add(store.name);
            }
          });
        });
        if (conflictingStores.size > 0) {
          const storeList = Array.from(conflictingStores);
          const storeText = storeList.length > 2 ? `${storeList.slice(0, 2).join(', ')} and ${storeList.length - 2} more` : storeList.join(' and ');
          setConflictWarning(`${brandName} already has bookings between Week ${start} and ${end} at ${storeText}. Proceeding will add additional slots.`);
        } else {
          setConflictWarning(null);
        }
      }
    } else {
      setConflictWarning(null);
    }
  }, [brandName, startWeek, endWeek, mode, stores]);
  if (!isOpen) return null;
  const hasContact = selectedContactId || isAddingContact && newContact.name && newContact.email;
  const isValid = selectedCount > 0 && selectedMediaTypes.length > 0 && startWeek !== '' && endWeek !== '' && Number(startWeek) <= Number(endWeek) && (mode === 'block' || brandName !== '' && hasContact);
  const toggleMediaType = (type: MediaType) => {
    setSelectedMediaTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };
  const handleSaveNewContact = () => {
    if (!newContact.name || !newContact.email) return;
    const contact: BrandContact = {
      id: `contact-${Date.now()}`,
      ...newContact
    };
    const updatedContacts = [...contacts, contact];
    setContacts(updatedContacts);
    setSelectedContactId(contact.id);
    setIsAddingContact(false);
    setNewContact({
      name: '',
      email: '',
      jobTitle: ''
    });
    const brand = brands.find(b => b.name === brandName);
    if (brand) {
      localStorage.setItem(`brand-contacts-${brand.id}`, JSON.stringify(updatedContacts));
    }
  };
  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    const weekNumbers = [];
    for (let i = Number(startWeek); i <= Number(endWeek); i++) {
      weekNumbers.push(i);
    }
    let contactInfo: {
      name: string;
      email: string;
      jobTitle?: string;
    } | undefined;
    if (mode === 'prebook') {
      if (selectedContactId) {
        const contact = contacts.find(c => c.id === selectedContactId);
        if (contact) {
          contactInfo = {
            name: contact.name,
            email: contact.email,
            jobTitle: contact.jobTitle
          };
        }
      } else if (isAddingContact && newContact.name && newContact.email) {
        contactInfo = {
          name: newContact.name,
          email: newContact.email,
          jobTitle: newContact.jobTitle
        };
        handleSaveNewContact();
      }
    }
    await new Promise(resolve => setTimeout(resolve, 600));
    onConfirm(selectedMediaTypes, weekNumbers, mode === 'prebook' ? brandName : undefined, contactInfo);
    setIsSubmitting(false);
    setSelectedMediaTypes([]);
    setStartWeek('');
    setEndWeek('');
    setBrandName('');
    setSelectedContactId('');
    setIsAddingContact(false);
    setNewContact({
      name: '',
      email: '',
      jobTitle: ''
    });
    onClose();
  };
  const title = mode === 'prebook' ? 'Book Confirmed Campaign' : 'Block Inventory';
  const Icon = mode === 'prebook' ? CalendarPlusIcon : LockIcon;
  return <>
      <div className="fixed inset-0 bg-black/40 z-50 transition-opacity" onClick={onClose} data-id="element-228" />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-229">
        <div className="bg-white w-full max-w-lg shadow-2xl pointer-events-auto flex flex-col max-h-[90vh] rounded-xl" data-id="element-230">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border" data-id="element-231">
            <div className="flex items-center gap-2.5" data-id="element-232">
              <div className="w-8 h-8 rounded-sm bg-primary/5 flex items-center justify-center" data-id="element-233">
                <Icon className="w-4 h-4 text-primary" data-id="element-234" />
              </div>
              <h2 className="text-lg font-semibold text-text-primary" data-id="element-235">
                {title}
              </h2>
            </div>
            <button onClick={onClose} className="p-1.5 text-text-tertiary hover:text-text-primary transition-colors rounded-sm hover:bg-surface" data-id="element-236">
              <XIcon className="w-5 h-5" data-id="element-237" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1" data-id="element-238">
            {/* Store count info */}
            <div className={`p-3 rounded-sm border ${selectedCount === 0 ? 'bg-urgent/5 border-urgent/30' : 'bg-surface-warm border-border'}`} data-id="element-239">
              <p className={`text-sm font-medium ${selectedCount === 0 ? 'text-urgent' : 'text-text-primary'}`} data-id="element-240">
                {selectedCount === 0 ? 'No stores selected — please select stores from the table first' : `Applying to ${selectedCount} selected store${selectedCount !== 1 ? 's' : ''}`}
              </p>
              {selectedCount > 0 && <p className="text-xs text-text-secondary mt-1" data-id="element-241">
                  Only currently available slots matching your criteria will be{' '}
                  {mode === 'prebook' ? 'booked' : 'blocked'}.
                </p>}
            </div>

            {/* Conflict Warning */}
            {conflictWarning && <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-2.5" data-id="element-242">
                <AlertCircleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-243" />
                <p className="text-xs text-amber-800 leading-relaxed" data-id="element-244">
                  {conflictWarning}
                </p>
              </div>}

            {/* Media Types */}
            <div data-id="element-245">
              <label className="block text-sm font-medium text-text-primary mb-2" data-id="element-246">
                Media Types <span className="text-urgent" data-id="element-247">*</span>
              </label>
              <div className="space-y-2" data-id="element-248">
                {allMediaTypes.map(type => <label key={type} className="flex items-center gap-2.5 p-2.5 border border-border rounded-sm hover:bg-surface cursor-pointer" data-id="element-249">
                    <input type="checkbox" checked={selectedMediaTypes.includes(type)} onChange={() => toggleMediaType(type)} className="w-4 h-4 rounded-sm border-border text-primary focus:ring-primary" data-id="element-250" />
                    <span className="text-sm text-text-primary" data-id="element-251">{type}</span>
                  </label>)}
              </div>
            </div>

            {/* Weeks */}
            <div data-id="element-252">
              <label className="block text-sm font-medium text-text-primary mb-2" data-id="element-253">
                Booking Period <span className="text-urgent" data-id="element-254">*</span>
              </label>
              <div className="grid grid-cols-2 gap-4" data-id="element-255">
                <Select placeholder="Start Week" value={startWeek.toString()} onChange={e => setStartWeek(Number(e.target.value))} options={weeks.map(w => ({
                value: w.toString(),
                label: `Week ${w}`
              }))} data-id="element-256" />
                <Select placeholder="End Week" value={endWeek.toString()} onChange={e => setEndWeek(Number(e.target.value))} options={weeks.map(w => ({
                value: w.toString(),
                label: `Week ${w}`
              }))} data-id="element-257" />
              </div>
              {startWeek !== '' && endWeek !== '' && Number(startWeek) > Number(endWeek) && <p className="text-xs text-urgent mt-1.5" data-id="element-258">
                    End week must be after start week.
                  </p>}
            </div>

            {/* Brand (Pre-book only) */}
            {mode === 'prebook' && <>
                <div data-id="element-259">
                  <label className="block text-sm font-medium text-text-primary mb-2" data-id="element-260">
                    Brand <span className="text-urgent" data-id="element-261">*</span>
                  </label>
                  <Select placeholder="Select Brand" value={brandName} onChange={e => setBrandName(e.target.value)} options={brands.map(b => ({
                value: b.name,
                label: b.name
              }))} data-id="element-262" />
                </div>

                {/* Contact Person */}
                {brandName && <div className="bg-surface-warm p-4 rounded-sm border border-border" data-id="element-263">
                    <div className="flex items-center justify-between mb-3" data-id="element-264">
                      <label className="block text-sm font-medium text-text-primary" data-id="element-265">
                        Contact Person <span className="text-urgent" data-id="element-266">*</span>
                      </label>
                      {!isAddingContact && <button onClick={() => setIsAddingContact(true)} className="text-xs font-medium text-primary hover:text-primary-dark flex items-center gap-1" data-id="element-267">
                          <PlusIcon className="w-3 h-3" data-id="element-268" />
                          Add New Contact
                        </button>}
                    </div>

                    {!isAddingContact && contacts.length > 0 ? <Select value={selectedContactId} onChange={e => setSelectedContactId(e.target.value)} options={contacts.map(c => ({
                value: c.id,
                label: `${c.name} (${c.email})${c.jobTitle ? ` — ${c.jobTitle}` : ''}`
              }))} data-id="element-269" /> : <div className="space-y-3 bg-white p-3 border border-border rounded-sm" data-id="element-270">
                        <div className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-1" data-id="element-271">
                          New Contact Details
                        </div>
                        <div className="grid grid-cols-2 gap-3" data-id="element-272">
                          <div data-id="element-273">
                            <label className="block text-xs text-text-secondary mb-1" data-id="element-274">
                              Name *
                            </label>
                            <input type="text" value={newContact.name} onChange={e => setNewContact({
                      ...newContact,
                      name: e.target.value
                    })} className="w-full px-3 py-1.5 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" placeholder="e.g. Jane Doe" data-id="element-275" />
                          </div>
                          <div data-id="element-276">
                            <label className="block text-xs text-text-secondary mb-1" data-id="element-277">
                              Email *
                            </label>
                            <input type="email" value={newContact.email} onChange={e => setNewContact({
                      ...newContact,
                      email: e.target.value
                    })} className="w-full px-3 py-1.5 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" placeholder="jane@example.com" data-id="element-278" />
                          </div>
                        </div>
                        <div data-id="element-279">
                          <label className="block text-xs text-text-secondary mb-1" data-id="element-280">
                            Job Title
                          </label>
                          <input type="text" value={newContact.jobTitle} onChange={e => setNewContact({
                    ...newContact,
                    jobTitle: e.target.value
                  })} className="w-full px-3 py-1.5 text-sm border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-primary" placeholder="e.g. Marketing Director" data-id="element-281" />
                        </div>
                        <div className="flex justify-end gap-2 mt-2" data-id="element-282">
                          {contacts.length > 0 && <Button variant="ghost" size="sm" onClick={() => setIsAddingContact(false)} data-id="element-283">
                              Cancel
                            </Button>}
                          <Button variant="secondary" size="sm" onClick={handleSaveNewContact} disabled={!newContact.name || !newContact.email} data-id="element-284">
                            Save Contact
                          </Button>
                        </div>
                      </div>}

                    {/* Confirmation copy notice */}
                    {hasContact && <div className="flex items-center gap-2 mt-3 p-2.5 bg-primary/5 border border-primary/20 rounded-sm" data-id="element-285">
                        <MailIcon className="w-4 h-4 text-primary flex-shrink-0" data-id="element-286" />
                        <span className="text-xs text-text-secondary" data-id="element-287">
                          A confirmation copy will be sent to the brand contact.
                        </span>
                      </div>}
                  </div>}
              </>}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border bg-surface flex items-center justify-between" data-id="element-288">
            <Button variant="ghost" onClick={onClose} data-id="element-289">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={!isValid || isSubmitting} className="flex items-center gap-2" data-id="element-290">
              {isSubmitting ? 'Processing...' : <>
                  <Icon className="w-4 h-4" data-id="element-291" />
                  {mode === 'prebook' ? 'Confirm Booking' : 'Confirm Block'}
                </>}
            </Button>
          </div>
        </div>
      </div>
    </>;
}